import { useCallback, useEffect, useState } from 'react';
import { Alert, Box, Button, Chip, CircularProgress, Stack, TextField, Typography } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import { useNotify, useRefresh } from 'react-admin';
import { SectionCard } from '../shared/Panel';
import { useFeature } from '../admin/featureFlags/FeatureGate';
import { useHasPermission } from '../admin/PermissionGate';
import { ContractorPicker } from './ContractorPicker';
import {
  FEATURE_PRE_ORDER_CONTRACTOR,
  fetchPreOrderContractor,
  setPreOrderContractor,
  toErrorMessage,
  type ContractorRef,
  type PreOrderContractorView,
  type VatSuggestionView,
} from './preOrderContractorApi';

/**
 * „Kontrahent z kartoteki” na karcie prezlecenia i w oknie kontroli
 * (makieta back office v2, fala B, 21.10.2026; przełącznik
 * `preOrderContractor`, S-21.10-10; BR-ADM-11, BR-ADM-13).
 *
 * Pokazuje wybranego kontrahenta, skąd kontrahent do podpowiedzi VAT
 * (wybrany na prezleceniu, a po połączeniu z TMS — z TMS) i ostrzeżenia:
 * inny kontrahent w TMS, kilka oddziałów z jednym NIP. „Wybierz z kartoteki”
 * / „Zmień” — przyjęcie zleceń, dział, u którego jest rekord; także po
 * akceptacji (kontrahent nie jest polem pliku dla VIA), do połączenia z TMS.
 * Wyłączony przełącznik — sekcji nie ma.
 */

/** Podpowiedź VAT słowem — chip, powód, braki; stawkę wpisuje Administracja w TMS. */
export const VatSuggestionLine = ({ vat }: { vat: VatSuggestionView }) => (
  <Stack spacing={0.5} data-testid="pre-order-contractor-vat" data-rate={vat.rate ?? ''}>
    <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
      <Typography variant="body2" sx={{ fontWeight: 500 }}>
        Podpowiedź VAT:
      </Typography>
      <Chip
        size="small"
        color={vat.rate ? (vat.certain ? 'success' : 'warning') : 'default'}
        label={vat.rate ?? 'brak danych'}
      />
      {!vat.certain && vat.rate ? (
        <Typography variant="caption" color="text.secondary">
          rekomendacja niepewna
        </Typography>
      ) : null}
    </Stack>
    <Typography variant="body2">{vat.reason}</Typography>
    {vat.missing.length > 0 ? (
      <Typography variant="caption" color="text.secondary">
        Brakuje: {vat.missing.join(', ')}
      </Typography>
    ) : null}
    <Typography variant="caption" color="text.secondary">
      Stawkę wpisuje Administracja w TMS — to podpowiedź, nie zapis.
    </Typography>
  </Stack>
);

export const PreOrderContractorSection = ({
  preOrderId,
  clientName,
  view: given,
  lockedReason = null,
  showVat = true,
  onChanged,
}: {
  preOrderId: number | string;
  /** Nazwa klienta z prezlecenia — od niej startuje wyszukiwarka. */
  clientName?: string | null;
  /** Widok z okna kontroli — bez osobnego odczytu. */
  view?: PreOrderContractorView | null;
  /** Rekord u drugiego działu (obieg) — zmiana wyłączona z powodem. */
  lockedReason?: string | null;
  /** W oknie kontroli VAT stoi w swojej sekcji — tu bez powtórki. */
  showVat?: boolean;
  onChanged?: (v: PreOrderContractorView) => void;
}) => {
  const on = useFeature(FEATURE_PRE_ORDER_CONTRACTOR);
  const canChange = useHasPermission('ORDER_INTAKE');
  const notify = useNotify();
  const refresh = useRefresh();
  const [fetched, setFetched] = useState<PreOrderContractorView | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [picked, setPicked] = useState<ContractorRef | null>(null);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      setFetched(await fetchPreOrderContractor(preOrderId));
      setError(null);
    } catch (e) {
      setError(toErrorMessage(e));
    }
  }, [preOrderId]);

  useEffect(() => {
    if (on && given === undefined) void load();
  }, [on, given, load]);

  if (!on) return null;
  const view = given ?? fetched;

  const startEdit = () => {
    setPicked(view?.chosen ?? null);
    setReason('');
    setEditing(true);
  };

  const save = (contractorId: number | null) =>
    void (async () => {
      setBusy(true);
      try {
        const next = await setPreOrderContractor(preOrderId, contractorId, reason);
        setFetched(next);
        setEditing(false);
        notify(contractorId === null ? 'Kontrahent zdjęty — zostaje nazwa klienta' : 'Kontrahent zapisany', {
          type: 'success',
        });
        // Okno kontroli przeładowuje się samo; karta — odświeżenie rekordu
        // (wpis w historii zmian).
        if (onChanged) onChanged(next);
        else refresh();
      } catch (e) {
        notify(toErrorMessage(e), { type: 'error' });
      } finally {
        setBusy(false);
      }
    })();

  const refusal = view?.changeRefusal ?? lockedReason;

  return (
    <SectionCard title="Kontrahent z kartoteki">
      <Box data-testid="pre-order-contractor" data-source={view?.source ?? ''}>
        {error ? <Alert severity="error">{error}</Alert> : null}
        {!view && !error ? <CircularProgress size={20} /> : null}
        {view ? (
          <Stack spacing={1}>
            <Typography variant="body2" data-testid="pre-order-contractor-chosen">
              {view.chosenLabel ?? (
                <>
                  Nie wybrano — {clientName ? `jest tylko nazwa klienta „${clientName}”` : 'brak nazwy klienta'}.
                </>
              )}
            </Typography>
            <Typography variant="caption" color="text.secondary" data-testid="pre-order-contractor-source">
              Kontrahent do podpowiedzi VAT: {view.effectiveLabel ? `${view.effectiveLabel} — ` : ''}
              {view.sourceLabel}
            </Typography>
            {view.mismatch ? (
              <Alert severity="warning" data-testid="pre-order-contractor-mismatch">
                {view.mismatch}
              </Alert>
            ) : null}
            {view.branchNote ? (
              <Alert severity="info" data-testid="pre-order-contractor-branches">
                {view.branchNote}
              </Alert>
            ) : null}
            {showVat ? <VatSuggestionLine vat={view.vat} /> : null}

            {canChange && !editing ? (
              <Box>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<EditIcon />}
                  disabled={busy || refusal !== null}
                  onClick={startEdit}
                  data-testid="pre-order-contractor-change"
                >
                  {view.chosen ? 'Zmień kontrahenta' : 'Wybierz z kartoteki'}
                </Button>
                {refusal ? (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                    {refusal}
                  </Typography>
                ) : null}
              </Box>
            ) : null}

            {editing ? (
              <Stack spacing={1} data-testid="pre-order-contractor-edit">
                <ContractorPicker value={picked} onChange={setPicked} suggestFrom={clientName} disabled={busy} />
                <TextField
                  size="small"
                  label="Powód (opcjonalnie)"
                  value={reason}
                  disabled={busy}
                  onChange={(e) => setReason(e.target.value)}
                  slotProps={{ htmlInput: { maxLength: 500, 'data-testid': 'pre-order-contractor-reason' } }}
                />
                <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: 'wrap', alignItems: 'center' }}>
                  <Button
                    size="small"
                    variant="contained"
                    disabled={busy || picked === null || picked.id === view.chosen?.id}
                    onClick={() => save(picked?.id ?? null)}
                    data-testid="pre-order-contractor-save"
                  >
                    Zapisz kontrahenta
                  </Button>
                  {view.chosen ? (
                    <Button
                      size="small"
                      color="warning"
                      disabled={busy}
                      onClick={() => save(null)}
                      data-testid="pre-order-contractor-clear"
                    >
                      Zostaw samą nazwę klienta
                    </Button>
                  ) : null}
                  <Button size="small" disabled={busy} onClick={() => setEditing(false)}>
                    Anuluj
                  </Button>
                  {busy ? <CircularProgress size={18} /> : null}
                </Stack>
                <Typography variant="caption" color="text.secondary">
                  Zmiana trafia do historii prezlecenia i zlecenia. Pliku dla VIA nie zmienia.
                </Typography>
              </Stack>
            ) : null}
          </Stack>
        ) : null}
      </Box>
    </SectionCard>
  );
};
